import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@core-ui/Components/ui/button';
import { Loader2, ArrowRight, Wifi, Play } from 'lucide-react';

import type { WizardStep } from '../types/wizard.types';
import type { StepMode } from '../store/useWizardStore';
import useToastMessage from '@lib/logger/toast.ts';
import { logInfo } from '@lib/logger/logInfo.ts';
import { useSessionStore } from '@features/auth';
import TestSoundInternetDialog from '@ui/shared/TestSoundInternetDialog.tsx';
import { Label } from '@core-ui/Components/ui/label.tsx';

interface WizardNavigationProps {
  currentStep: WizardStep;
  canProceed: boolean;
  isLoading?: boolean;
  isEditingFromSummary?: boolean;
  stepMode?: StepMode;
  canCreate?: boolean;
  onPrevious: () => void;
  onNext: () => void;
  onSubmit?: () => void;
  onBackToSummary?: () => void;
  onCreateAndContinue?: () => void;
}

export default function WizardNavigation ({
  currentStep,
  canProceed,
  isLoading = false,
  isEditingFromSummary = false,
  stepMode = 'select',
  canCreate = false,
  onPrevious,
  onNext,
  onSubmit,
  onBackToSummary,
  onCreateAndContinue,
}: WizardNavigationProps): React.JSX.Element {
  const { t } = useTranslation();
  const isFirstStep = currentStep === 1;
  const isLastStep = currentStep === 5;
  const { showToast } = useToastMessage();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isInternetTest, setIsInternetTest] = useState(false);

  const currentUser = useSessionStore().currentUser;

  const isCreateMode = stepMode === 'create';

  const handleNextOrSubmit = async (): Promise<void> => {
    if (isCreateMode && onCreateAndContinue !== undefined) {
      onCreateAndContinue();
      return;
    }

    if (isLastStep && onSubmit !== undefined) {
      onSubmit();
      showToast('warning', `${t('wizard.dashboard.startedToast')}`);
      await logInfo(`Session created by user ${currentUser?.userId}`, 'wizardNavigation', {}, false);
    } else {
      onNext();
    }
  };

  const getButtonLabel = (): string => {
    if (isLastStep) {
      return t('wizard.navigation.createSession');
    }
    if (isCreateMode) {
      return t('wizard.navigation.createAndContinue', 'Create & Continue');
    }
    return t('wizard.navigation.next');
  };

  const isDisabled = isCreateMode
    ? !canCreate || isLoading
    : !canProceed || isLoading;

  return (
    <div className="flex items-center justify-between pt-6">
      {currentStep !== 1 ? (<Button
        data-qa-id="previous-step"
        variant="outline"
        onClick={onPrevious}
        disabled={isFirstStep || isLoading}
        className="min-w-[100px] border border-border text-foreground hover:bg-muted"
      >
        {t('wizard.navigation.previous')}
      </Button>) : (<div />)}

      <div className="flex items-center justify-center">
        {isLastStep && (
          <div className='flex items-center justify-center gap-2'>
            <Button
              className="rounded-lg border border-border/60 bg-muted/20 hover:bg-muted/50 text-muted-foreground hover:text-foreground transition-all flex items-center gap-3"
              onClick={() => {
                setIsDialogOpen(true);
                setIsInternetTest(false);
              }}
              disabled={false}
            >
              <Play className="h-4 w-4 text-sky-400"/>
              <Label className="text-md md:text-md text-foreground cursor-pointer">
                {t('agentLayout.testSound')}
              </Label>
            </Button>

            <Button
              className="rounded-lg border border-border/60 bg-muted/20 hover:bg-muted/50 text-muted-foreground hover:text-foreground transition-all flex items-center gap-3"
              onClick={() => {
                setIsDialogOpen(true);
                setIsInternetTest(true);
              }}
            >
              <Wifi className="h-4 w-4 text-sky-400" />
              <Label className="text-md text-foreground cursor-pointer">
                {t('agentLayout.testConnection')}
              </Label>
            </Button>
          </div>
        )}
      </div>

      <div className="flex items-center gap-3">
        {isEditingFromSummary && !isLastStep && (
          <Button
            data-qa-id="back-to-summary"
            variant="outline"
            onClick={onBackToSummary}
            disabled={!canProceed || isLoading}
            className="min-w-[140px] gap-2 border border-border text-foreground hover:bg-muted"
          >
            {t('wizard.navigation.backToSummary')}
            <ArrowRight className="h-4 w-4" />
          </Button>
        )}

        <Button
          data-qa-id="next-step"
          onClick={() => {
            void handleNextOrSubmit();
          }}
          disabled={isDisabled}
          className="min-w-[140px] gap-2 bg-primary hover:bg-primary/90 text-primary-foreground cursor-pointer"
        >
          {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
          {getButtonLabel()}
        </Button>
      </div>

      <TestSoundInternetDialog
        open={isDialogOpen}
        setOpen={setIsDialogOpen}
        isInternetTest={isInternetTest}
      />
    </div>
  );
}
